<script setup lang="ts">
import ChapterNumberingControls from "./ChapterNumberingControls.vue";
import { computed } from "vue";
import { useChapterHeadings } from "../../composables/useChapterHeadings";
import { andThenMaybe, isJust, isNothing, nothing } from "../../lib/maybe";
import { scenePath } from "../../lib/zwConfig";
import { trimmedMaybe } from "../../lib/trimmedString";
import { requestMapLandmarkFocus } from "../../lib/mapLandmarkFocus";
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
import { useDisplayModeStore } from "../../stores/useDisplayModeStore";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import { useSceneMetadata } from "../../composables/useSceneMetadata";
import SceneMetadata, {
  type StoryboardEntityChip,
} from "../storyboard/SceneMetadata.vue";
import SceneLinkPicker from "./SceneLinkPicker.vue";
import ContextPanel from "./ContextPanel.vue";
import ZwTextarea from "../ui/ZwTextarea.vue";

const { activeSceneLabel } = useChapterHeadings();
const session = useEditorSessionStore();
const display = useDisplayModeStore();
const workspace = useProjectWorkspaceStore();
const metadata = useSceneMetadata();
const info = computed(() =>
  isJust(session.trashPreview) ? nothing : session.activeSceneInfo,
);
const path = computed(() =>
  isJust(info.value)
    ? scenePath(info.value.value.chapter.folder, info.value.value.sceneFile)
    : "",
);
const chips = computed(() =>
  path.value.length > 0 ? metadata.chipsFor(path.value) : [],
);

function openChip(chip: StoryboardEntityChip): void {
  const file = andThenMaybe(
    chip.kind === "map" ? chip.mapFile : chip.entryFile,
    (value) => trimmedMaybe(value),
  );
  if (isNothing(file)) return;
  const landmark = andThenMaybe(chip.landmarkId, (value) =>
    trimmedMaybe(value),
  );
  if (chip.kind === "map" && isJust(landmark))
    requestMapLandmarkFocus(file.value, landmark.value);
  void workspace.onSelectProjectDatabase(file.value);
  if (session.mobileShell) session.contextPanelOpen = false;
}
</script>
<template>
  <ContextPanel
    v-if="display.isWriting"
    :title="activeSceneLabel"
    body-class=""
  >
    <template #actions>
      <ChapterNumberingControls v-if="isJust(info)" />
    </template>
    <SceneMetadata
      v-if="isJust(info)"
      class="scene-details-metadata"
      :scene-path="path"
      :entity-chips="chips"
      :on-chip-click="openChip"
      :on-arc-chip-pointer-down="nothing"
    >
      <template #actions
        ><SceneLinkPicker :key="path" :scene-path="path" :chips="chips"
      /></template>
      <ZwTextarea
        :id="nothing"
        v-model="session.synopsis"
        class="scene-details-synopsis"
        :label="nothing"
        variant="plain"
        autosize
        :rows="1"
        placeholder="Add synopsis..."
        aria-label="Synopsis"
      />
    </SceneMetadata>
    <p v-else class="empty-details">Select a scene to view its details.</p>
  </ContextPanel>
</template>
<style scoped>
.scene-details-metadata {
  align-items: stretch;
  gap: var(--space-lg);
  padding: var(--space-md) 0;
}
.scene-details-metadata :deep(.zw-board-map-preview) {
  align-self: center;
}
.scene-details-synopsis :deep(.zw-input),
.scene-details-synopsis :deep(.zw-input-face-text) {
  font-family: var(--font-ui);
  font-size: 0.8rem;
  line-height: 1.6;
}
.scene-details-metadata :deep(.zw-board-card-chips) {
  justify-content: flex-start;
  gap: var(--space-xs);
}
.scene-details-metadata :deep(.zw-board-card-chip) {
  text-align: left;
}
.empty-details {
  padding: var(--space-md) var(--space-sm);
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.5;
}
</style>
